import { useEffect, useState } from "react";
import { Button, Dropdown, Form, Icon, Message, Modal, Segment } from "semantic-ui-react";
import { bookingApi } from "../../api/api";
import { mapMailTemplate, mapMailPreview } from "../../api/mappers";

/* Whether the dialog may submit: a template is chosen, sending isn't blocked
   (no employee id to address it to), and an amount-requiring template has a
   positive amount typed. Exported and unit-tested on its own, the same way
   this codebase tests other small decision functions without a full render. */
export function canSendMail({ template, templates, blockedReason, amount }) {
  const selected = templates.find((t) => t.code === template);
  const needsAmount = !!selected?.requiresAmount;
  return !!template && !blockedReason && (!needsAmount || Number(amount) > 0);
}

/* Shared by the booking detail panel's own "Send Mail" button and the
   page-level "send a mail?" prompt shown after an action that didn't mail
   the applicant automatically — both need the same template picker, preview
   and send call, so there is exactly one implementation of it.

   `onSend` does the actual sending (AdminDashboard's `handleSendMail`,
   already wired to show the page-level toast and rethrow on failure) —
   this component only owns the template/preview UI around that call. */
export default function SendMailModal({
  open,
  bookingId,
  empId,
  heading = "Send Mail to Applicant",
  helperText = "",
  defaultTemplate = "",
  onSend,
  onClose,
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [blockedReason, setBlockedReason] = useState(null);
  const [template, setTemplate] = useState("");
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !bookingId) return;
    setTemplate(defaultTemplate);
    setAmount("");
    setPreview(null);
    setError("");
    let cancelled = false;
    (async () => {
      try {
        const data = await bookingApi.mailTemplates(bookingId);
        if (cancelled) return;
        const mapped = (data.templates || []).map(mapMailTemplate);
        setTemplates(mapped);
        setBlockedReason(data.blocked_reason || null);
        // Only keep the preselection if that template is actually offered
        // (e.g. not hidden/blocked for this booking).
        if (defaultTemplate && !mapped.some((t) => t.code === defaultTemplate)) {
          setTemplate("");
        }
      } catch (e) {
        if (cancelled) return;
        setTemplates([]);
        setError(e.message || "Could not load the mail templates.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bookingId, defaultTemplate]);

  const selectedTemplate = templates.find((t) => t.code === template);
  const needsAmount = !!selectedTemplate?.requiresAmount;
  const canSend = canSendMail({ template, templates, blockedReason, amount });

  /* Debounced preview: the amount field would otherwise re-fire this on every
     keystroke. ~400ms matches the debounce this codebase already uses for a
     field that re-fires a request against a weak server. */
  useEffect(() => {
    if (!open || !template || blockedReason) {
      setPreview(null);
      return;
    }
    if (needsAmount && !(Number(amount) > 0)) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError("");
    const t = setTimeout(async () => {
      try {
        const data = await bookingApi.mailPreview(bookingId, template, needsAmount ? amount : undefined);
        setPreview(mapMailPreview(data));
      } catch (e) {
        setPreview(null);
        setPreviewError(e.message || "Could not render a preview.");
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [open, bookingId, template, amount, needsAmount, blockedReason]);

  if (!open) return null;

  const handleSend = async () => {
    setSending(true);
    setError("");
    try {
      await onSend(bookingId, template, amount);
      onSent?.();
    } catch (e) {
      setError(e.message || "The mail was not sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open size="small" onClose={onClose}>
      <Modal.Header>
        <Icon name="mail outline" />
        {heading}
      </Modal.Header>
      {/* Not `scrolling`: that sets overflow:auto here, which clips the
         template dropdown's menu to the content box. */}
      <Modal.Content>
        {helperText && (
          <p style={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.6)", marginTop: 0 }}>{helperText}</p>
        )}
        {blockedReason && <Message warning content={blockedReason} />}
        <Form>
          <Form.Field>
            <label>Template</label>
            <Dropdown
              selection
              fluid
              placeholder="Choose a template"
              options={templates.map((t) => ({
                key: t.code, value: t.code, text: t.label,
              }))}
              value={template}
              onChange={(e, { value }) => {
                setTemplate(value);
                setError("");
              }}
            />
          </Form.Field>
          {needsAmount && (
            <Form.Field>
              <label>One-day advance (₹)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="e.g. 2500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Form.Field>
          )}
        </Form>

        {template && !blockedReason && (
          <div style={{ marginTop: 12 }}>
            {previewLoading ? (
              <span style={{ fontSize: "0.82rem", opacity: 0.6 }}>Rendering preview…</span>
            ) : previewError ? (
              <Message size="tiny" negative content={previewError} />
            ) : preview ? (
              <Segment>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{preview.subject}</div>
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0, fontSize: "0.85rem" }}>
                  {preview.body}
                </pre>
              </Segment>
            ) : null}
          </div>
        )}

        <p style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.5)", marginTop: 12 }}>
          Sent to the applicant, addressed by employee id {empId}.
        </p>
        {error && <Message negative content={error} />}
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} type="button" disabled={sending}>
          Cancel
        </Button>
        <Button
          color="teal"
          onClick={handleSend}
          loading={sending}
          disabled={sending || !canSend}
          type="button"
        >
          <Icon name="send" />
          Send
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
